/* ==========================================================================
   COGNITRON — SITE BEHAVIOR
   Handles: mobile nav toggle, stat count-up, scroll entrance animations.
   No framework, no build step — plain JS for a static site.
   ========================================================================== */

document.addEventListener('DOMContentLoaded', function () {
  initNavToggle();
  initStatCounters();
  initScrollAnimations();
  initAccordions();
  initTestimonialCarousel();
  initPublicationsToggle();
  initContactForm();
});

/* --------------------------------------------------------------------------
   1. MOBILE NAV TOGGLE
   -------------------------------------------------------------------------- */
function initNavToggle() {
  const toggle = document.querySelector('.nav-toggle');
  const mobileMenu = document.querySelector('.mobile-menu');

  if (!toggle || !mobileMenu) return;

  toggle.addEventListener('click', function () {
    const isOpen = mobileMenu.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', String(isOpen));
    document.body.classList.toggle('nav-open', isOpen);
  });

  // Close on link click
  mobileMenu.querySelectorAll('a').forEach(function (link) {
    link.addEventListener('click', function () {
      closeMobileMenu(toggle, mobileMenu);
    });
  });

  // Close on outside click
  document.addEventListener('click', function (event) {
    const isOpen = mobileMenu.classList.contains('is-open');
    if (!isOpen) return;
    const clickedInsideMenu = mobileMenu.contains(event.target);
    const clickedToggle = toggle.contains(event.target);
    if (!clickedInsideMenu && !clickedToggle) {
      closeMobileMenu(toggle, mobileMenu);
    }
  });

  // Close on Escape
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      closeMobileMenu(toggle, mobileMenu);
    }
  });
}

function closeMobileMenu(toggle, mobileMenu) {
  mobileMenu.classList.remove('is-open');
  toggle.setAttribute('aria-expanded', 'false');
  document.body.classList.remove('nav-open');
}

/* --------------------------------------------------------------------------
   2. STAT COUNT-UP
   Expects markup like:
   <span class="stat-figure" data-count-to="1000000" data-suffix="+" data-format="comma">0</span>
   -------------------------------------------------------------------------- */
function initStatCounters() {
  const statEls = document.querySelectorAll('[data-count-to]');
  if (!statEls.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion) {
    // Skip animation entirely; just render final values.
    statEls.forEach(function (el) {
      el.textContent = formatStatValue(el);
    });
    return;
  }

  const observer = new IntersectionObserver(
    function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          animateCount(entry.target);
          obs.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.4 }
  );

  statEls.forEach(function (el) {
    observer.observe(el);
  });
}

function animateCount(el) {
  const target = parseInt(el.getAttribute('data-count-to'), 10);
  if (isNaN(target)) return;

  const duration = 1400; // ms
  const startTime = performance.now();

  function tick(now) {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
    const current = Math.round(target * eased);

    el.textContent = formatStatValue(el, current);

    if (progress < 1) {
      requestAnimationFrame(tick);
    } else {
      el.textContent = formatStatValue(el, target);
    }
  }

  requestAnimationFrame(tick);
}

function formatStatValue(el, valueOverride) {
  const target = parseInt(el.getAttribute('data-count-to'), 10);
  const value = valueOverride !== undefined ? valueOverride : target;
  const suffix = el.getAttribute('data-suffix') || '';
  const useComma = el.getAttribute('data-format') === 'comma';
  const formatted = useComma ? value.toLocaleString('en-US') : String(value);
  return formatted + suffix;
}

/* --------------------------------------------------------------------------
   3. SCROLL ENTRANCE ANIMATIONS
   Expects elements with [data-animate] attribute.
   Respects prefers-reduced-motion by skipping the hidden/observed state
   entirely — elements are visible immediately (see CSS guard below).
   -------------------------------------------------------------------------- */
function initScrollAnimations() {
  const animatedEls = document.querySelectorAll('[data-animate]');
  if (!animatedEls.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return; // CSS shows elements by default in this case

  const observer = new IntersectionObserver(
    function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
  );

  animatedEls.forEach(function (el) {
    observer.observe(el);
  });
}

/* --------------------------------------------------------------------------
   4. ACCORDIONS
   Expects [data-accordion-item] wrapping a [data-accordion-trigger] button
   and a .accordion-panel. Clicking a trigger toggles .is-open on its item.
   Each accordion group (by shared parent) allows multiple open items,
   matching the prototype's independent per-item toggle behavior.
   -------------------------------------------------------------------------- */
function initAccordions() {
  const triggers = document.querySelectorAll('[data-accordion-trigger]');
  if (!triggers.length) return;

  triggers.forEach(function (trigger) {
    trigger.addEventListener('click', function () {
      const item = trigger.closest('[data-accordion-item]');
      if (!item) return;
      item.classList.toggle('is-open');
    });
  });
}

/* --------------------------------------------------------------------------
   5. TESTIMONIAL CAROUSEL
   Auto-rotates every 6s with a fade transition, matching the prototype.
   Dots allow manual navigation; manual clicks reset the auto-rotate timer.
   -------------------------------------------------------------------------- */
function initTestimonialCarousel() {
  const quoteEl = document.getElementById('testimonial-quote');
  const nameEl = document.getElementById('testimonial-name');
  const roleEl = document.getElementById('testimonial-role');
  const dots = document.querySelectorAll('.testimonial-dot');
  if (!quoteEl || !dots.length) return;

  // Placeholder content — real quotes/names removed pending consent
  // verification. Section is currently commented out in HTML anyway,
  // so this array has no visible effect until both are restored together.
  const testimonials = [
    {
      quote: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
      name: "Lorem Ipsum",
      role: "Placeholder Title, Placeholder Organisation"
    },
    {
      quote: "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
      name: "Lorem Ipsum",
      role: "Placeholder Title, Placeholder Organisation"
    },
    {
      quote: "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.",
      name: "Lorem Ipsum",
      role: "Placeholder Title, Placeholder Organisation"
    },
    {
      quote: "Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
      name: "Lorem Ipsum",
      role: "Placeholder Title, Placeholder Organisation"
    }
  ];

  let index = 0;
  let timer = null;

  function render(i) {
    const t = testimonials[i];
    quoteEl.textContent = '\u201C' + t.quote + '\u201D';
    if (nameEl) nameEl.textContent = t.name;
    if (roleEl) roleEl.textContent = t.role;
    dots.forEach(function (dot, dotIndex) {
      dot.classList.toggle('is-active', dotIndex === i);
    });
  }

  function fadeTo(i) {
    quoteEl.classList.add('testimonial-fading');
    const attribution = document.getElementById('testimonial-attribution');
    if (attribution) attribution.classList.add('testimonial-fading');
    setTimeout(function () {
      render(i);
      quoteEl.classList.remove('testimonial-fading');
      if (attribution) attribution.classList.remove('testimonial-fading');
    }, 500);
  }

  function startAutoRotate() {
    if (timer) return;
    timer = setInterval(function () {
      index = (index + 1) % testimonials.length;
      fadeTo(index);
    }, 6000);
  }

  function stopAutoRotate() {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  dots.forEach(function (dot) {
    dot.addEventListener('click', function () {
      const i = parseInt(dot.getAttribute('data-index'), 10);
      if (isNaN(i) || i === index) return;
      stopAutoRotate();
      index = i;
      fadeTo(index);
      startAutoRotate();
    });
  });

  startAutoRotate();
}

/* --------------------------------------------------------------------------
   6. PUBLICATIONS SHOW MORE
   Toggles visibility of the extra publications list and swaps the
   trigger label between "Show more" / "Show less".
   -------------------------------------------------------------------------- */
function initPublicationsToggle() {
  const toggle = document.querySelector('[data-publications-toggle]');
  const more = document.querySelector('[data-publications-more]');
  if (!toggle || !more) return;

  toggle.addEventListener('click', function () {
    const isVisible = more.classList.toggle('is-visible');
    toggle.textContent = isVisible ? 'Show less' : 'Show more publications';
  });
}

/* --------------------------------------------------------------------------
   7. CONTACT FORM
   No backend is wired up, so this builds a mailto: link from the field
   values and opens the visitor's email client with the message pre-filled
   to webenquiries@cognitron.co.uk. The visitor still has to hit send in their
   own mail app — this is not a silent server-side send. Replace with a
   real form-handling endpoint (or a service like Formspree) before launch
   if silent/logged submissions are needed.
   -------------------------------------------------------------------------- */
function initContactForm() {
  const form = document.querySelector('[data-contact-form]');
  if (!form) return;

  const RECIPIENT = 'webenquiries@cognitron.co.uk';

  form.addEventListener('submit', function (event) {
    event.preventDefault();

    const name = (form.querySelector('#field-name') || {}).value || '';
    const email = (form.querySelector('#field-email') || {}).value || '';
    const org = (form.querySelector('#field-org') || {}).value || '';
    const sector = (form.querySelector('#field-sector') || {}).value || '';
    const message = (form.querySelector('#field-message') || {}).value || '';

    const subject = 'Cognitron enquiry from ' + (name || 'website visitor');
    const bodyLines = [
      'Name: ' + name,
      'Email: ' + email,
      'Organisation: ' + org,
      'Sector: ' + sector,
      '',
      'Message:',
      message
    ];
    const body = bodyLines.join('\n');

    const mailtoUrl =
      'mailto:' + encodeURIComponent(RECIPIENT) +
      '?subject=' + encodeURIComponent(subject) +
      '&body=' + encodeURIComponent(body);

    window.location.href = mailtoUrl;

    const note = form.querySelector('[data-form-note]');
    if (note) {
      note.textContent = 'Opening your email app with this message pre-filled to ' + RECIPIENT + ' — hit send there to finish.';
    }
  });
}